import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from '../auth/guards/auth.guard';
import { WarehousesComponent } from './pages/warehouses/warehouses.component';
import { WarehousesNewComponent } from './pages/warehouses-new/warehouses-new.component';
import { WarehousesEditComponent } from './pages/warehouses-edit/warehouses-edit.component';
import { StorageRatesComponent } from './pages/storage-rates/storage-rates.component';
import { StorageChargesComponent } from './pages/storage-charges/storage-charges.component';
import { StorageRatesNewComponent } from './pages/storage-rates-new/storage-rates-new.component';
import { StorageRatesEditComponent } from './pages/storage-rates-edit/storage-rates-edit.component';
import { LocationsComponent } from './pages/locations/locations.component';
import { LocationsNewComponent } from './pages/locations-new/locations-new.component';
import { LocationsEditComponent } from './pages/locations-edit/locations-edit.component';
import { ProductGroupsComponent } from './pages/product-groups/product-groups.component';
import { ProductGroupsNewComponent } from './pages/product-groups-new/product-groups-new.component';
import { ProductGroupsEditComponent } from './pages/product-groups-edit/product-groups-edit.component';
import { ProductClassesComponent } from './pages/product-classes/product-classes.component';
import { ProductClassesNewComponent } from './pages/product-classes-new/product-classes-new.component';
import { ProductClassesEditComponent } from './pages/product-classes-edit/product-classes-edit.component';
import { StockLevelsComponent } from './pages/stock-levels/stock-levels.component';
import { StockMovementsComponent } from './pages/stock-movements/stock-movements.component';
import { GoodsInComponent } from './pages/goods-in/goods-in.component';
import { GoodsOutComponent } from './pages/goods-out/goods-out.component';
import { StockAdjustmentComponent } from './pages/stock-adjustment/stock-adjustment.component';
import { StockTransferComponent } from './pages/stock-transfer/stock-transfer.component';
import { StockMovementLabelsComponent } from './pages/stock-movement-labels/stock-movement-labels.component';
import { LocationsLabelsComponent } from './pages/locations-labels/locations-labels.component';
import { LocationsLabelComponent } from './pages/locations-label/locations-label.component';
import { ProductsLabelComponent } from './pages/products-label/products-label.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { ReportsComponent } from './pages/reports/reports.component';
import { ReportStockOnHandComponent } from './pages/report-stock-on-hand/report-stock-on-hand.component';
import { ReportMovementHistoryComponent } from './pages/report-movement-history/report-movement-history.component';
import { ReportAdjustmentsComponent } from './pages/report-adjustments/report-adjustments.component';
import { ReportReconciliationComponent } from './pages/report-reconciliation/report-reconciliation.component';
import { ReportBatchTraceabilityComponent } from './pages/report-batch-traceability/report-batch-traceability.component';
import { ReportSlowMovingComponent } from './pages/report-slow-moving/report-slow-moving.component';
import { CostHistoryComponent } from './pages/cost-history/cost-history.component';
import { StockRevaluationComponent } from './pages/stock-revaluation/stock-revaluation.component';
import { AssistantComponent } from './pages/assistant/assistant.component';


const routes: Routes = [
  // The dashboard is the module's landing screen, so a bare /warehousing lands there
  // rather than on nothing. pathMatch full so it cannot shadow the child routes.
  {
    path: 'warehousing',
    redirectTo: 'warehousing/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'warehousing/dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/warehouses',
    component: WarehousesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/warehouses/new',
    component: WarehousesNewComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/warehouses/edit/:id',
    component: WarehousesEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/storage-rates',
    component: StorageRatesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/storage-charges',
    component: StorageChargesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/storage-rates/new',
    component: StorageRatesNewComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/storage-rates/edit/:id',
    component: StorageRatesEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/locations',
    component: LocationsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/locations/new',
    component: LocationsNewComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/locations/edit/:id',
    component: LocationsEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/product-groups',
    component: ProductGroupsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/product-groups/new',
    component: ProductGroupsNewComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/product-groups/edit/:id',
    component: ProductGroupsEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/product-classes',
    component: ProductClassesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/product-classes/new',
    component: ProductClassesNewComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/product-classes/edit/:id',
    component: ProductClassesEditComponent,
    canActivate: [AuthGuard],
  },
  // No product-batches routes. A batch is not a record that can be created: there is no batch table,
  // and a batch number exists exactly as long as a movement carries it. The screens those routes pointed
  // at are still on disk but are wired to nothing — they queried an entity the backend never exposed, so
  // reaching them only ever produced a 404 under copy asserting rules this project decided against.
  // "Which batches exist" is answered by the batch traceability report, which reads the ledger.
  {
    path: 'warehousing/stock-levels',
    component: StockLevelsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/stock-movements',
    component: StockMovementsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/goods-in',
    component: GoodsInComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/goods-out',
    component: GoodsOutComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/adjustment',
    component: StockAdjustmentComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/transfer',
    component: StockTransferComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/stock-movement-labels',
    component: StockMovementLabelsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/locations/labels',
    component: LocationsLabelsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/locations/label/:id',
    component: LocationsLabelComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/products/label/:id',
    component: ProductsLabelComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports',
    component: ReportsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports/stock-on-hand',
    component: ReportStockOnHandComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports/movement-history',
    component: ReportMovementHistoryComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports/adjustments',
    component: ReportAdjustmentsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports/reconciliation',
    component: ReportReconciliationComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports/batch-traceability',
    component: ReportBatchTraceabilityComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/reports/slow-moving',
    component: ReportSlowMovingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/cost-history',
    component: CostHistoryComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/stock-revaluation',
    component: StockRevaluationComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'warehousing/assistant',
    component: AssistantComponent,
    canActivate: [AuthGuard],
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class WarehousingRoutingModule { }
